import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
	Alert,
	AlertTitle,
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	Divider,
	Grid,
	IconButton,
	LinearProgress,
	List,
	ListItem,
	ListItemText,
	Pagination,
	Paper,
	Stack,
	Stepper,
	Step,
	StepContent,
	StepLabel,
	TextField,
	Tooltip,
	Typography,
	alpha,
} from "@mui/material";
import {
	ArrowBack,
	CheckCircle,
	ContentCopy,
	Error,
	Person,
	PlayArrow,
	Refresh,
	Schedule,
	SkipNext,
	Stop,
	Storage,
	Timer,
	Visibility,
	VisibilityOff,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { motion } from "framer-motion";
import executionService from "../services/executionService";
import socketService from "../services/socketService";
import StepCheckDetails, { CHECK_STEP_LABELS, checkStepSummary } from "../components/StepCheckDetails";
import FailureAnalysisPanel from "../components/FailureAnalysisPanel";
import LiveBrowserView from "../components/LiveBrowserView";
import {
	elapsedMs,
	formatDateTime,
	formatDateTimeShort,
	formatElapsed,
} from "../utils/dateUtils";

// QUEUED: a run waiting for a free slot (Settings → System limits how many run
// at once, system-wide) or for its turn in a suite that runs tests one at a time
const RUNNING_STATUSES = ["QUEUED", "PENDING", "RUNNING"];

// Executor socket events that end a run, and the status each one means
const FINAL_EVENT_STATUS = {
	completed: "SUCCESS",
	success: "SUCCESS",
	failed: "FAILED",
	cancelled: "CANCELLED",
};

// How far an unfinished run has got. A run loaded as QUEUED or PENDING is moved
// on by its live events and the poll, never back: the backend can still say
// PENDING after the executor has started (its "running" update is in flight).
const RUN_PROGRESS = { QUEUED: 0, PENDING: 1, RUNNING: 2 };
const advanceStatus = (execution, status) =>
	execution &&
	RUN_PROGRESS[execution.status] != null &&
	RUN_PROGRESS[status] > RUN_PROGRESS[execution.status]
		? { ...execution, status }
		: execution;

const deriveExecutionStatus = (execution) => {
	const stepsOk =
		execution?.stepResults?.length > 0 &&
		execution.stepResults.every((s) => {
			const stepStatus = (s.status || "").toUpperCase();
			return (
				stepStatus === "SUCCESS" ||
				stepStatus === "PASSED" ||
				stepStatus === "SKIPPED"
			);
		});
	if (execution?.status === "PENDING" && stepsOk) {
		return { ...execution, status: "SUCCESS" };
	}
	return execution;
};

export default function Executions() {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const { enqueueSnackbar } = useSnackbar();
	// The mount effect's interval and socket handlers outlive the render that
	// created them — read the current selection through this ref, not state.
	const selectedExecutionRef = useRef(null);
	const subscribedIds = useRef(new Set());

	const [executions, setExecutions] = useState([]);
	const [selectedExecution, setSelectedExecution] = useState(null);
	const [loading, setLoading] = useState(true);
	const [liveStatus, setLiveStatus] = useState({});
	const [searchQuery, setSearchQuery] = useState("");
	const [currentPage, setCurrentPage] = useState(1);
	const [itemsPerPage] = useState(10);
	const [stoppingId, setStoppingId] = useState(null);

	useEffect(() => {
		selectedExecutionRef.current = selectedExecution;
	}, [selectedExecution]);

	const testId = searchParams.get("testId");
	const executionId = searchParams.get("executionId");

	useEffect(() => {
		loadExecutions();

		const socket = socketService.connect();
		socket.on("connect", () =>
			console.log("Connected to executor WebSocket"),
		);

		// Fallback for missed socket events: a fast failure (e.g. browser launch
		// error) can emit "failed" before we subscribe or while the socket is
		// still connecting, and Socket.io does not replay. The backend is the
		// source of truth, so check it and reload once the run has finished.
		const pollInterval = setInterval(async () => {
			const current = selectedExecutionRef.current;
			if (!current || !RUNNING_STATUSES.includes(current.status)) return;
			try {
				const latest = deriveExecutionStatus(
					await executionService.getExecution(current.id),
				);
				if (!RUNNING_STATUSES.includes(latest.status)) {
					loadExecutions();
				} else {
					setRunStatus(latest.id, latest.status);
				}
			} catch (error) {
				// Transient — retried on the next tick
			}
		}, 2000);

		return () => {
			clearInterval(pollInterval);
			setLiveStatus({}); // Clear live status on unmount
			subscribedIds.current.forEach((id) => {
				socket.off(`execution:${id}`);
				socketService.unwatchExecution(id);
			});
			subscribedIds.current.clear();
			socketService.disconnect();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [testId, executionId]);

	const loadExecutions = async () => {
		try {
			setLoading(true);

			let focusedExecution = null;
			if (executionId) {
				focusedExecution = deriveExecutionStatus(
					await executionService.getExecution(executionId),
				);
				if (RUNNING_STATUSES.includes(focusedExecution.status)) {
					subscribeToExecution(focusedExecution.id);
				}
			}

			const params = testId
				? { testId, page: 0, size: 20 }
				: { page: 0, size: 20 };
			const response = await executionService.getExecutions(params);
			let executionList = (response.content || []).map(
				deriveExecutionStatus,
			);

			if (focusedExecution) {
				executionList = [
					focusedExecution,
					...executionList.filter(
						(e) => e.id !== focusedExecution.id,
					),
				];
			}

			executionList = [...executionList].sort(
				(a, b) =>
					new Date(b.startedAt || 0) - new Date(a.startedAt || 0),
			);

			setExecutions(executionList);

			const runningExecution = executionList.find((e) =>
				RUNNING_STATUSES.includes(e.status),
			);
			if (runningExecution) {
				subscribeToExecution(runningExecution.id);
			}
			// The execution named in the URL wins — otherwise any other RUNNING
			// row (even a stuck one) would steal the view once it finishes.
			if (focusedExecution) {
				setSelectedExecution(focusedExecution);
			} else if (runningExecution) {
				setSelectedExecution(runningExecution);
			} else if (selectedExecutionRef.current) {
				// Preserve currently selected execution if it exists in the new list
				const stillExists = executionList.find(
					(e) => e.id === selectedExecutionRef.current.id,
				);
				setSelectedExecution(stillExists || executionList[0]);
			} else if (executionList.length > 0) {
				setSelectedExecution(executionList[0]);
			}
		} catch (error) {
			console.error("Error loading executions:", error);
			enqueueSnackbar("Failed to load executions", { variant: "error" });
		} finally {
			setLoading(false);
		}
	};

	const setRunStatus = (id, status) => {
		setSelectedExecution((prev) =>
			prev && prev.id === id ? advanceStatus(prev, status) : prev,
		);
		setExecutions((prev) =>
			prev.map((e) => (e.id === id ? advanceStatus(e, status) : e)),
		);
	};

	const subscribeToExecution = (id) => {
		const socket = socketService.connect();
		// loadExecutions can subscribe the same id several times (focused and
		// running paths, repeated loads) — keep exactly one handler per id.
		socket.off(`execution:${id}`);
		subscribedIds.current.add(id);
		socketService.watchExecution(id);
		socket.on(`execution:${id}`, (data) => {
			console.log(`Received live update for execution ${id}:`, data);
			setLiveStatus((prev) => ({ ...prev, [id]: data }));

			// Any event but the last means the executor is running it. Before the
			// step results below: a PENDING run whose steps all passed reads as
			// finished (deriveExecutionStatus), which hid the live view.
			if (!FINAL_EVENT_STATUS[data.event]) setRunStatus(id, "RUNNING");

			// Update step results in real-time when step is completed or failed
			if (
				data.event === "step_completed" ||
				data.event === "step_failed" ||
				data.event === "step_skipped"
			) {
				setSelectedExecution((prev) => {
					if (!prev || prev.id !== id) return prev;

					const stepIndex = data.data?.stepIndex;
					if (stepIndex === undefined) return prev;

					const updatedStepResults = [...(prev.stepResults || [])];
					const existingIndex = updatedStepResults.findIndex(
						(r) =>
							r.stepIndex === stepIndex ||
							r.metadata?.stepIndex === stepIndex ||
							parseInt(r.stepId, 10) === stepIndex,
					);

					const newStepResult = {
						stepId: String(stepIndex),
						stepIndex: stepIndex,
						type: data.data?.step?.type,
						selector: data.data?.step?.selector,
						status:
							data.event === "step_completed"
								? "SUCCESS"
								: data.event === "step_skipped"
									? "SKIPPED"
									: "FAILED",
						timestamp: data.timestamp || new Date().toISOString(),
						errorMessage: data.data?.error || null,
						screenshot: data.data?.screenshot || null,
						metadata: {
							stepIndex: stepIndex,
							value: data.data?.step?.value || null,
							url: data.data?.step?.url || null,
							recovery: data.data?.recovery || null,
							screenshot: data.data?.screenshot || null,
						},
					};

					if (existingIndex >= 0) {
						updatedStepResults[existingIndex] = newStepResult;
					} else {
						updatedStepResults.push(newStepResult);
					}

					return {
						...prev,
						stepResults: updatedStepResults,
					};
				});
			}

			const finalStatus = FINAL_EVENT_STATUS[data.event];
			if (finalStatus) {
				setSelectedExecution((prev) =>
					prev && prev.id === id
						? {
								...prev,
								status: finalStatus,
								completedAt:
									data.timestamp || new Date().toISOString(),
							}
						: prev,
				);

				setExecutions((prev) =>
					prev.map((e) =>
						e.id === id
							? {
									...e,
									status: finalStatus,
									completedAt:
										data.timestamp || e.completedAt,
								}
							: e,
					),
				);

				setTimeout(() => loadExecutions(), 1000);
			}
		});
	};

	const handleStopExecution = async (id) => {
		setStoppingId(id);
		try {
			const stopped = deriveExecutionStatus(
				await executionService.stopExecution(id),
			);
			setSelectedExecution((prev) =>
				prev && prev.id === id ? { ...prev, ...stopped } : prev,
			);
			setExecutions((prev) =>
				prev.map((e) => (e.id === id ? { ...e, ...stopped } : e)),
			);
			enqueueSnackbar("Execution stopped", { variant: "info" });
		} catch (error) {
			const status = error.response?.status;
			enqueueSnackbar(
				status === 409
					? "Execution had already finished"
					: status === 403
						? "You can only stop your own executions"
						: "Failed to stop execution",
				{ variant: status === 409 ? "info" : "error" },
			);
			loadExecutions();
		} finally {
			setStoppingId(null);
		}
	};

	const getStatusChip = (status) => {
		switch (status) {
			case "SUCCESS":
				return { color: "success", icon: <CheckCircle /> };
			case "FAILED":
				return { color: "error", icon: <Error /> };
			case "RUNNING":
				return { color: "info", icon: <PlayArrow /> };
			case "QUEUED":
			case "PENDING":
				return { color: "warning", icon: <Schedule /> };
			case "CANCELLED":
				return { color: "default", icon: <Stop /> };
			default:
				return { color: "default", icon: <Schedule /> };
		}
	};

	const filteredExecutions = executions.filter((e) => {
		if (!searchQuery.trim()) return true;
		const q = searchQuery.toLowerCase();
		return (
			e.id?.toLowerCase().includes(q) ||
			e.testName?.toLowerCase().includes(q) ||
			e.status?.toLowerCase().includes(q) ||
			e.userName?.toLowerCase().includes(q)
		);
	});

	// Reset to page 1 when search query changes
	useEffect(() => {
		setCurrentPage(1);
	}, [searchQuery]);

	// Pagination logic
	const totalPages = Math.ceil(filteredExecutions.length / itemsPerPage);
	const startIndex = (currentPage - 1) * itemsPerPage;
	const endIndex = startIndex + itemsPerPage;
	const paginatedExecutions = filteredExecutions.slice(startIndex, endIndex);

	const handlePageChange = (event, value) => {
		setCurrentPage(value);
	};

	const copyToClipboard = (text) => {
		navigator.clipboard.writeText(text);
		enqueueSnackbar("Copied to clipboard", { variant: "success" });
	};

	return (
		<Box>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 4,
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
					{testId && (
						<IconButton onClick={() => navigate("/tests")}>
							<ArrowBack />
						</IconButton>
					)}
					<Box>
						<Typography variant="h4" fontWeight={700}>
							Test Executions
						</Typography>
						<Typography
							variant="body2"
							color="text.secondary"
							sx={{ mt: 0.5 }}
						>
							View execution results and live status
						</Typography>
					</Box>
				</Box>
				<IconButton onClick={loadExecutions}>
					<Refresh />
				</IconButton>
			</Box>

			{loading ? (
				<Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
					<CircularProgress />
				</Box>
			) : executions.length === 0 ? (
				<Card
					sx={{
						textAlign: "center",
						py: 8,
						background: (theme) =>
							`linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
					}}
				>
					<CardContent>
						<Typography variant="h6" gutterBottom>
							No executions yet
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Run a test to see execution results here
						</Typography>
					</CardContent>
				</Card>
			) : (
				<Grid container spacing={3}>
					<Grid item xs={12} md={4}>
						<Card>
							<CardContent>
								<Box
									sx={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
										mb: 2,
									}}
								>
									<Typography variant="h6">
										Execution History
									</Typography>
									<Chip
										label={`${filteredExecutions.length} total`}
										size="small"
										color="primary"
										variant="outlined"
									/>
								</Box>
								<TextField
									fullWidth
									size="small"
									placeholder="Search by ID, test, user, status..."
									value={searchQuery}
									onChange={(e) =>
										setSearchQuery(e.target.value)
									}
									sx={{ mb: 2 }}
								/>
								<List
									sx={{
										maxHeight: "calc(100vh - 350px)",
										overflow: "auto",
									}}
								>
									{paginatedExecutions.length === 0 ? (
										<Box
											sx={{
												textAlign: "center",
												py: 4,
											}}
										>
											<Typography
												variant="body2"
												color="text.secondary"
											>
												No executions found
											</Typography>
										</Box>
									) : (
										paginatedExecutions.map((execution) => {
											const chip = getStatusChip(
												execution.status,
											);
											return (
												<ListItem
													key={execution.id}
													button
													selected={
														selectedExecution?.id ===
														execution.id
													}
													onClick={() => {
														setSelectedExecution(
															execution,
														);
														if (
															RUNNING_STATUSES.includes(
																execution.status,
															)
														) {
															subscribeToExecution(
																execution.id,
															);
														}
													}}
													sx={{
														borderRadius: 1,
														mb: 1,
														border: 1,
														borderColor:
															selectedExecution?.id ===
															execution.id
																? "primary.main"
																: "divider",
														bgcolor:
															selectedExecution?.id ===
															execution.id
																? (theme) =>
																		alpha(
																			theme
																				.palette
																				.primary
																				.main,
																			0.08,
																		)
																: "transparent",
														"&:hover": {
															borderColor:
																"primary.light",
														},
													}}
												>
													<ListItemText
														primary={
															<Stack
																spacing={0.5}
															>
																<Box
																	sx={{
																		display:
																			"flex",
																		alignItems:
																			"center",
																		justifyContent:
																			"space-between",
																	}}
																>
																	<Chip
																		label={
																			execution.status
																		}
																		size="small"
																		color={
																			chip.color
																		}
																		icon={
																			chip.icon
																		}
																	/>
																	<Typography
																		variant="caption"
																		color="text.secondary"
																	>
																		{execution.startedAt
																			? formatDateTimeShort(execution.startedAt)
																			: "N/A"}
																	</Typography>
																</Box>
																<Typography
																	variant="body2"
																	fontWeight={
																		600
																	}
																	noWrap
																	sx={{
																		overflow:
																			"hidden",
																		textOverflow:
																			"ellipsis",
																	}}
																>
																	{execution.testName ||
																		"Unnamed Test"}
																</Typography>
																<Box
																	sx={{
																		display:
																			"flex",
																		alignItems:
																			"center",
																		gap: 0.5,
																	}}
																>
																	<Tooltip title="Execution ID">
																		<Typography
																			variant="caption"
																			color="text.secondary"
																			sx={{
																				fontFamily:
																					"monospace",
																				fontSize:
																					"0.7rem",
																				cursor: "pointer",
																				"&:hover":
																					{
																						color: "primary.main",
																					},
																			}}
																			onClick={(
																				e,
																			) => {
																				e.stopPropagation();
																				copyToClipboard(
																					execution.id,
																				);
																			}}
																		>
																			{execution.id?.substring(
																				0,
																				12,
																			)}
																			...
																		</Typography>
																	</Tooltip>
																</Box>
																{execution.userName && (
																	<Box
																		sx={{
																			display:
																				"flex",
																			alignItems:
																				"center",
																			gap: 0.5,
																		}}
																	>
																		<Person
																			sx={{
																				fontSize:
																					"0.875rem",
																				color: "text.secondary",
																			}}
																		/>
																		<Typography
																			variant="caption"
																			color="text.secondary"
																		>
																			{
																				execution.userName
																			}
																		</Typography>
																	</Box>
																)}
																{execution.dataSetName && (
																	<Box
																		sx={{
																			display:
																				"flex",
																			alignItems:
																				"center",
																			gap: 0.5,
																		}}
																	>
																		<Storage
																			sx={{
																				fontSize:
																					"0.875rem",
																				color: "text.secondary",
																			}}
																		/>
																		<Typography
																			variant="caption"
																			color="text.secondary"
																		>
																			{
																				execution.dataSetName
																			}
																			{execution.dataRowIndex !=
																				null &&
																				` (row ${execution.dataRowIndex + 1})`}
																		</Typography>
																	</Box>
																)}
															</Stack>
														}
													/>
												</ListItem>
											);
										})
									)}
								</List>
								{totalPages > 1 && (
									<>
										<Divider sx={{ my: 2 }} />
										<Box
											sx={{
												display: "flex",
												justifyContent: "center",
											}}
										>
											<Pagination
												count={totalPages}
												page={currentPage}
												onChange={handlePageChange}
												color="primary"
												size="small"
												showFirstButton
												showLastButton
											/>
										</Box>
									</>
								)}
							</CardContent>
						</Card>
					</Grid>

					<Grid item xs={12} md={8}>
						{selectedExecution && (
							<ExecutionDetails
								execution={selectedExecution}
								liveStatus={liveStatus[selectedExecution.id]}
								onStop={handleStopExecution}
								stopping={stoppingId === selectedExecution.id}
							/>
						)}
					</Grid>
				</Grid>
			)}
		</Box>
	);
}

function formatStepDuration(ms) {
	if (ms == null) return null;
	if (ms < 1000) return `${ms}ms`;
	return `${(ms / 1000).toFixed(1)}s`;
}

// Live view on/off is remembered per browser
const LIVE_VIEW_KEY = "executionLiveView";
const readLiveViewPreference = () => {
	try {
		return localStorage.getItem(LIVE_VIEW_KEY) === "on";
	} catch {
		return false;
	}
};

function ExecutionDetails({ execution, liveStatus, onStop, stopping }) {
	const normalizedExecution = deriveExecutionStatus(execution);
	const isRunning = RUNNING_STATUSES.includes(normalizedExecution.status);
	const currentStepIndex = liveStatus?.data?.stepIndex ?? -1;

	// Tick once a second while running so the elapsed time stays live
	const [now, setNow] = useState(Date.now());
	useEffect(() => {
		if (!isRunning) return undefined;
		const timer = setInterval(() => setNow(Date.now()), 1000);
		return () => clearInterval(timer);
	}, [isRunning]);

	const [liveView, setLiveView] = useState(readLiveViewPreference);
	const toggleLiveView = () => {
		setLiveView((on) => {
			try {
				localStorage.setItem(LIVE_VIEW_KEY, on ? "off" : "on");
			} catch {
				// Not remembered, still toggled
			}
			return !on;
		});
	};

	// Measured from the displayed timestamps so the three values agree; the
	// stored duration is the fallback (it is taken when the backend receives
	// the result, so it runs slightly long).
	const measuredMs = elapsedMs(
		normalizedExecution.startedAt,
		normalizedExecution.completedAt || (isRunning ? now : null),
	);
	const totalMs =
		measuredMs != null && measuredMs >= 0
			? measuredMs
			: normalizedExecution.duration ?? null;

	// Normalize screenshots - remove all whitespace and newlines from base64 strings
	const normalizeScreenshot = (shot) => {
		if (!shot) return null;
		if (typeof shot === "string") {
			// Remove all whitespace, newlines, and carriage returns from base64 string
			return shot.replace(/[\s\r\n]+/g, "");
		}
		if (shot?.data && Array.isArray(shot.data)) {
			const bytes = new Uint8Array(shot.data);
			let binary = "";
			const chunkSize = 0x8000;
			for (let i = 0; i < bytes.length; i += chunkSize) {
				binary += String.fromCharCode.apply(
					null,
					bytes.subarray(i, i + chunkSize),
				);
			}
			return btoa(binary);
		}
		return null;
	};

	const steps = normalizedExecution.steps?.length
		? normalizedExecution.steps
		: liveStatus?.data?.results?.map((r) => r.step).filter(Boolean) || [];

	// An API test (only apiRequest steps) runs without a browser
	const usesBrowser =
		!steps.length ||
		steps.some((s) => !s.disabled && s.type !== "apiRequest");
	const showLiveToggle = isRunning && usesBrowser;

	const rawStepResults =
		normalizedExecution.stepResults || normalizedExecution.results || [];

	const stepResults = rawStepResults.map((r, idx) => {
		const normalizedMeta = normalizeScreenshot(r.metadata?.screenshot);
		const normalizedDirect = normalizeScreenshot(r.screenshot);

		return {
			...r,
			screenshot: normalizedDirect,
			metadata: {
				...r.metadata,
				screenshot: normalizedMeta,
			},
		};
	});

	// Check multiple possible error field locations
	const storedError =
		normalizedExecution.errorMessage ||
		normalizedExecution.error ||
		execution.errorMessage ||
		execution.error;

	const displayedError = liveStatus?.data?.error ?? storedError;

	const chip = (() => {
		switch (normalizedExecution.status) {
			case "SUCCESS":
				return { color: "success", icon: <CheckCircle /> };
			case "FAILED":
				return { color: "error", icon: <Error /> };
			case "RUNNING":
				return { color: "info", icon: <PlayArrow /> };
			case "QUEUED":
			case "PENDING":
				return { color: "warning", icon: <Schedule /> };
			case "CANCELLED":
				return { color: "default", icon: <Stop /> };
			default:
				return { color: "default", icon: <Schedule /> };
		}
	})();

	return (
		<motion.div
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.3 }}
		>
			<Card>
				<CardContent>
					<Box sx={{ mb: 3 }}>
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								mb: 2,
							}}
						>
							<Typography variant="h5" fontWeight={600}>
								Execution Details
							</Typography>
							<Stack direction="row" spacing={1} alignItems="center">
								{showLiveToggle && (
									<Tooltip title="Watch the test's browser while it runs">
										<Button
											size="small"
											variant={liveView ? "contained" : "outlined"}
											startIcon={liveView ? <VisibilityOff /> : <Visibility />}
											onClick={toggleLiveView}
										>
											{liveView ? "Hide live view" : "Live view"}
										</Button>
									</Tooltip>
								)}
								{/* The runner, the test's owner and admins (backend: canStop) */}
								{isRunning && execution.canStop !== false && (
									<Button
										size="small"
										variant="outlined"
										color="error"
										startIcon={
											stopping ? (
												<CircularProgress size={14} color="inherit" />
											) : (
												<Stop />
											)
										}
										disabled={stopping}
										onClick={() => onStop(normalizedExecution.id)}
									>
										{stopping ? "Stopping…" : "Stop"}
									</Button>
								)}
								<Chip
									label={normalizedExecution.status}
									color={chip.color}
									icon={chip.icon}
								/>
							</Stack>
						</Box>

						{console.log(
							"Current execution data: ",
							normalizedExecution,
							liveStatus,
							"storedError:",
							storedError,
							"displayedError:",
							displayedError,
						)}

						{isRunning && (
							<Box sx={{ mb: 2 }}>
								<LinearProgress />
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ mt: 1 }}
								>
									Executing step {currentStepIndex + 1} of{" "}
									{steps.length || 0}
								</Typography>
							</Box>
						)}

						{showLiveToggle && liveView && (
							<LiveBrowserView
								executionId={normalizedExecution.id}
								status={normalizedExecution.status}
							/>
						)}

						<Grid container spacing={2}>
							<Grid item xs={6} sm={4}>
								<Typography
									variant="caption"
									color="text.secondary"
								>
									Started At
								</Typography>
								<Typography variant="body2">
									{normalizedExecution.startedAt
										? formatDateTime(normalizedExecution.startedAt)
										: ""}
								</Typography>
							</Grid>
							{normalizedExecution.completedAt && (
								<Grid item xs={6} sm={4}>
									<Typography
										variant="caption"
										color="text.secondary"
									>
										Completed At
									</Typography>
									<Typography variant="body2">
										{formatDateTime(normalizedExecution.completedAt)}
									</Typography>
								</Grid>
							)}
							{totalMs != null && (
								<Grid item xs={6} sm={4}>
									<Typography
										variant="caption"
										color="text.secondary"
									>
										{isRunning ? "Elapsed" : "Total Time"}
									</Typography>
									<Typography
										variant="body2"
										data-testid="execution-total-time"
									>
										{formatElapsed(totalMs)}
									</Typography>
								</Grid>
							)}
						</Grid>

						{normalizedExecution.dataSetName && (
							<Box
								sx={{
									mt: 2,
									p: 1.5,
									bgcolor: "action.hover",
									borderRadius: 1,
								}}
							>
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 1,
										mb: 1,
									}}
								>
									<Storage fontSize="small" color="primary" />
									<Typography variant="subtitle2">
										Data-Driven:{" "}
										{normalizedExecution.dataSetName}
									</Typography>
									{normalizedExecution.dataRowIndex !=
										null && (
										<Chip
											label={`Row ${normalizedExecution.dataRowIndex + 1}`}
											size="small"
											color="primary"
											variant="outlined"
										/>
									)}
									{normalizedExecution.batchId && (
										<Chip
											label="Batch"
											size="small"
											color="secondary"
											variant="outlined"
										/>
									)}
								</Box>
								{normalizedExecution.dataValues &&
									Object.keys(normalizedExecution.dataValues)
										.length > 0 && (
										<Box
											sx={{
												display: "flex",
												gap: 0.5,
												flexWrap: "wrap",
											}}
										>
											{Object.entries(
												normalizedExecution.dataValues,
											).map(([key, value]) => (
												<Chip
													key={key}
													label={`${key}: ${value}`}
													size="small"
													variant="outlined"
													sx={{
														fontFamily: "monospace",
														fontSize: "0.7rem",
													}}
												/>
											))}
										</Box>
									)}
							</Box>
						)}
					</Box>

					{normalizedExecution.status === "CANCELLED" && (
						<Alert severity="info" sx={{ mb: 3 }}>
							<AlertTitle>Execution Stopped</AlertTitle>
							{storedError || "Stopped before it finished."}
						</Alert>
					)}

					{(normalizedExecution.status === "FAILED" ||
						liveStatus?.event === "failed") &&
						displayedError && (
							<Alert severity="error" sx={{ mb: 3 }}>
								<AlertTitle>Execution Failed</AlertTitle>
								{displayedError}
							</Alert>
						)}

					{normalizedExecution.status === "FAILED" && (
						<FailureAnalysisPanel
							key={normalizedExecution.id}
							executionId={normalizedExecution.id}
							testId={normalizedExecution.testId}
							completedAt={normalizedExecution.completedAt}
						/>
					)}

					<Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
						Test Steps
					</Typography>

					{console.log("DEBUG: stepResults", stepResults)}
					{console.log("DEBUG: steps", steps)}

					<Stepper orientation="vertical" nonLinear>
						{steps.map((step, index) => {
							const stepResult = stepResults.find((r) => {
								const metaIndex = r.metadata?.stepIndex;
								const idIndex = r.stepId
									? parseInt(r.stepId, 10)
									: null;
								return (
									metaIndex === index ||
									r.stepIndex === index ||
									idIndex === index
								);
							});

							console.log(
								`DEBUG: Step ${index} - stepResult:`,
								stepResult,
								"screenshot:",
								stepResult?.screenshot,
								"metadata.screenshot:",
								stepResult?.metadata?.screenshot,
							);

							const normalizedStatus = stepResult?.status
								?.toString()
								.toUpperCase();
							const isActive =
								currentStepIndex === index && isRunning;
							const isCompleted =
								normalizedStatus === "SUCCESS" ||
								normalizedStatus === "PASSED";
							const isFailed = normalizedStatus === "FAILED";
							const isSkipped = normalizedStatus === "SKIPPED";
							console.log(
								`DEBUG: Step ${index} - isFailed:`,
								isFailed,
								"normalizedStatus:",
								normalizedStatus,
							);
							const currentLiveStatus = liveStatus;
							const liveFailed =
								currentLiveStatus?.event === "step_failed" &&
								currentLiveStatus?.data?.stepIndex === index;

							// Determine if step content should be expanded
							// Show content for active, failed, or completed steps
							const shouldExpand =
								isActive ||
								isFailed ||
								liveFailed ||
								isCompleted;

							return (
								<Step
									key={index}
									active={isActive}
									completed={isCompleted}
									expanded={shouldExpand}
								>
									<StepLabel
										error={isFailed || liveFailed}
										icon={
											isFailed || liveFailed ? (
												<Error color="error" />
											) : isSkipped ? (
												<SkipNext color="disabled" />
											) : isCompleted ? (
												<CheckCircle color="success" />
											) : isActive ? (
												<CircularProgress size={20} />
											) : (
												index + 1
											)
										}
									>
										<Box>
											<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
												<Typography
													variant="body1"
													fontWeight={500}
												>
													{CHECK_STEP_LABELS[step.type] ||
														step.type
															.charAt(0)
															.toUpperCase() +
															step.type.slice(1)}
												</Typography>
												{isSkipped && (
													<Chip label="Skipped" size="small" />
												)}
												{stepResult?.metadata?.recovery && (
													<Chip
														label="Recovered"
														size="small"
														color="warning"
														variant="outlined"
													/>
												)}
												{stepResult?.duration != null && (
													<Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
														<Timer sx={{ fontSize: 12, color: "text.disabled" }} />
														<Typography
															variant="caption"
															color="text.disabled"
														>
															{formatStepDuration(stepResult.duration)}
														</Typography>
													</Box>
												)}
											</Box>
											{step.selector && (
												<Typography
													variant="caption"
													color="text.secondary"
													sx={{
														fontFamily: "monospace",
													}}
												>
													{step.selector}
												</Typography>
											)}
											{stepResult?.metadata?.recovery && (
												<Typography
													variant="caption"
													color="warning.main"
													display="block"
												>
													Recovered: {stepResult.metadata.recovery}
												</Typography>
											)}
											{(step.url || step.value) && (
												<Typography
													variant="caption"
													color="text.secondary"
													display="block"
												>
													{step.url || step.value}
												</Typography>
											)}
											{CHECK_STEP_LABELS[step.type] && (
												<Typography variant="caption" color="text.secondary" display="block">
													{checkStepSummary(step)}
												</Typography>
											)}
										</Box>
									</StepLabel>
									<StepContent>
										{stepResult && (
											<Paper
												elevation={0}
												sx={{
													p: 2,
													bgcolor: (theme) =>
														alpha(
															theme.palette[isFailed ? "error" : "success"].main,
															theme.palette.mode === "dark" ? 0.14 : 0.08,
														),
													borderLeft: 3,
													borderColor: isFailed
														? "error.main"
														: "success.main",
													color: "text.primary",
												}}
											>
												{isFailed &&
													(stepResult.errorMessage ||
														stepResult.error) && (
														<>
															<Typography
																variant="caption"
																fontWeight={600}
															>
																Error:
															</Typography>
															<Typography variant="body2">
																{stepResult.errorMessage ||
																	stepResult.error}
															</Typography>
														</>
													)}

												{isCompleted && (
													<Typography variant="body2">
														Step completed
														successfully
													</Typography>
												)}
												<StepCheckDetails
													stepResult={stepResult}
													executionId={normalizedExecution.id}
													stepIndex={index}
												/>
											</Paper>
										)}

										{/* Only show screenshot for failed steps */}
										{(isFailed || liveFailed) &&
											(() => {
												// Get screenshot from various sources and normalize it
												const rawScreenshot =
													stepResult?.metadata
														?.screenshot ||
													stepResult?.screenshot ||
													(currentLiveStatus?.event ===
														"step_failed" &&
														currentLiveStatus?.data
															?.stepIndex ===
															index &&
														currentLiveStatus?.data
															?.screenshot);

												// Normalize the screenshot (clean whitespace/newlines)
												const screenshot =
													normalizeScreenshot(
														rawScreenshot,
													);

												return screenshot ? (
													<Box sx={{ mt: 2 }}>
														<Typography
															variant="caption"
															color="error"
															fontWeight={600}
															display="block"
															sx={{ mb: 1 }}
														>
															Failure Screenshot
														</Typography>
														<Paper
															elevation={2}
															sx={{
																p: 1,
																bgcolor:
																	"action.hover",
																maxHeight: 400,
																overflow:
																	"auto",
																display: "flex",
																justifyContent:
																	"center",
																alignItems:
																	"center",
																minHeight: 200,
																border: "2px solid",
																borderColor:
																	"error.main",
															}}
														>
															<img
																src={`data:image/jpeg;base64,${screenshot}`}
																alt={`Step ${index + 1} failure screenshot`}
																style={{
																	width: "100%",
																	height: "auto",
																	display:
																		"block",
																}}
															/>
														</Paper>
													</Box>
												) : null;
											})()}

										{currentLiveStatus?.event ===
											"step_failed" &&
											currentLiveStatus?.data
												?.stepIndex === index && (
												<Paper
													elevation={0}
													sx={{
														p: 2,
														bgcolor: (theme) =>
															alpha(
																theme.palette.error.main,
																theme.palette.mode === "dark" ? 0.14 : 0.08,
															),
														borderLeft: 3,
														borderColor: "error.main",
														color: "text.primary",
														mt: 1,
													}}
												>
													<Typography
														variant="caption"
														fontWeight={600}
													>
														Error:
													</Typography>
													<Typography variant="body2">
														{
															currentLiveStatus
																.data.error
														}
													</Typography>
												</Paper>
											)}
									</StepContent>
								</Step>
							);
						})}
					</Stepper>
				</CardContent>
			</Card>
		</motion.div>
	);
}
